import { z } from "zod";

import { branded } from "@/lib/helpers/branded-helper.ts";
import {
  siteInstallationCommunicationTypeSchema,
  siteInstallationFlowSchema,
  siteInstallationStatusSchema,
  subscriberSignatureStatusSchema,
} from "@/types/backoffice/dto/signature/signature-details-dto";

export enum ESiteInstallationFlowType {
  PROVISION = "PROVISION",
  SALE = "SALE",
}

export enum ESiteCommunicationType {
  PAPER = "PAPER",
  ELECTRONIC = "ELECTRONIC",
}

export enum ESiteInstallationFlowStatus {
  ONGOING = "ONGOING",
  DONE = "DONE",
}

export enum ESignatureEnvelopeType {
  ESC_ELECTRONIC = "ESC_ELECTRONIC",
  ESC_PAPER = "ESC_PAPER",
  PROVISION_ELECTRONIC = "PROVISION_ELECTRONIC",
  PRE_INTERVENTION_ESC_QUOTE = "PRE_INTERVENTION_ESC_QUOTE",
}

export const siteInstallationFlowTypeSchema = z.nativeEnum(ESiteInstallationFlowType);
export const siteCommunicationType = z.nativeEnum(ESiteCommunicationType);
export const siteInstallationFlowStatus = z.nativeEnum(ESiteInstallationFlowStatus);

export const siteInstallationFlowDtoSchema = z.object({
  flow: siteInstallationFlowTypeSchema.nullable(),
  communicationType: siteCommunicationType.nullable(),
  status: siteInstallationFlowStatus.nullable(),
});

export type TSiteInstallationFlowType = z.infer<typeof siteInstallationFlowTypeSchema>;
export type TSiteCommunicationType = z.infer<typeof siteCommunicationType>;
export type TSiteInstallationFlowStatus = z.infer<typeof siteInstallationFlowStatus>;
export type TSiteInstallationFlowDto = z.infer<typeof siteInstallationFlowDtoSchema>;

export enum InstallationSignatureAction {
  CANCEL_SIGNATURE = "CANCEL_SIGNATURE",
  REGENERATE_SIGNATURE = "REGENERATE_SIGNATURE",
  SEND_REMINDER_FOR_SIGNATURE = "SEND_REMINDER_FOR_SIGNATURE",
  GENERATE_ESC_UPGRADE_SIGNATURE = "GENERATE_ESC_UPGRADE_SIGNATURE",

  // Deprecated types but we wouldn't want the frontend to crash because they're not supported
  ESC_ELECTRONIC_INTERVENTION_NOT_ENDED = "ESC_ELECTRONIC_INTERVENTION_NOT_ENDED",
  ESC_ELECTRONIC_FOR_MULTIPLE_INTERVENTION_ENDED = "ESC_ELECTRONIC_FOR_MULTIPLE_INTERVENTION_ENDED",
}

const installationSignatureDtoSchema = z.object({
  id: branded.escSubscriberSignatureId,
  status: subscriberSignatureStatusSchema,
  type: z.nativeEnum(ESignatureEnvelopeType),
  signatureCreationDate: z.coerce.date(),
  subscriberSignatureDate: z.coerce.date().nullable(),
  actions: z.array(z.nativeEnum(InstallationSignatureAction)),
});

export type TInstallationSignatureDto = z.infer<typeof installationSignatureDtoSchema>;

export enum EscValidationStatus {
  AWAITING_VALIDATION = "AWAITING_VALIDATION",
  VALIDATED = "VALIDATED",
}

export enum PaperFlowActions {
  GENERATE_ESC_UPGRADE_SIGNATURE = "GENERATE_ESC_UPGRADE_SIGNATURE",
}

const paperFlowInfoDtoSchema = z.object({
  installationSheetUploadDate: z.coerce.date().nullable(),
  escQuoteUploadDate: z.coerce.date().nullable(),
  escValidationStatus: z.nativeEnum(EscValidationStatus).nullable(),
  actions: z.nativeEnum(PaperFlowActions).array(),
});

export type TPaperFlowInfoDto = z.infer<typeof paperFlowInfoDtoSchema>;

export const siteInstallationStatusDtoSchema = z.object({
  flow: siteInstallationFlowSchema,
  communicationType: siteInstallationCommunicationTypeSchema,
  status: siteInstallationStatusSchema,
  electronicInstallationSignature: installationSignatureDtoSchema.nullable(),
  paperFlowInfo: paperFlowInfoDtoSchema.nullable(),
});

export type TSiteInstallationStatusDto = z.infer<typeof siteInstallationStatusDtoSchema>;
